/**
 * /dashboard/people — read-only company directory (Phase 3c —
 * 2026-06-01).
 *
 * Was the People page in the Next.js app. The directory itself is over
 * GET /api/v1/directory (auth-only for ANY tenant user — a line manager
 * or plain employee, not just HR; NOT feature-gated; non-PII roster
 * fields only). The "Add employee" / "Edit" affordances are role-gated
 * (company_admin / hr_manager) — the backend remains the boundary and
 * 403s a non-HR caller hitting POST/PATCH /employees regardless.
 *
 * Search filters by name/designation/department; a department <Select>
 * scopes the list. Each row links the person's manager to that manager's
 * detail page (a real typed <Link> to /people/$id, NOT a dead #person
 * in-page anchor — docs/51 manager-link bug) so the reporting line is
 * navigable. The manager name resolves via a full-roster useDirectory()
 * so it shows even when the manager isn't on the current (filtered/paged)
 * set, falling back to a neutral "User #<id>" only if truly unknown.
 *
 * Pagination: ?page=N (default 1) with a fixed per-page of 10. The
 * backend GET /api/v1/directory accepts ?page=N&per_page=M and returns
 * total so the UI can show Prev/Next controls and a "Showing X–Y of Z"
 * count.
 *
 * Search + department filter are SERVER-SIDE (bug B-A6, docs/51): the
 * search box (debounced) sends ?q= and the dropdown sends ?department=,
 * so filtering spans the WHOLE roster, not just the loaded 10-row page
 * (the old client-side filter only saw the current page, so searching a
 * colleague on page 2 wrongly returned "No people match"). Both filters
 * live in the URL (?q=&department=) so they survive refresh / sharing,
 * and a new filter resets to page 1. The department dropdown is populated
 * from the backend's tenant-wide DISTINCT `departments` list, so it
 * includes departments that only appear on later pages.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/people/page.tsx. Swaps:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link (typed). The Edit link
 *     uses params: { id } against the typed route
 *     /dashboard/people/$id/edit (registered in the same batch).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, Navigate, useParams, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { ChevronLeft, ChevronRight, Edit3, ListChecks, Search, SearchX, Trash2, Upload, UserPlus, Users } from "lucide-react";
import { directoryKeys, useDirectory } from "@/hooks/use-directory";
import { useDepartments } from "@/hooks/use-departments";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useAuth, usePoshOnly } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import type { Role } from "@/types/auth";
import { directoryApi, toApiError } from "@/lib/api";
import { EmployeeExportMenu } from "@/components/people/employee-export-menu";
import { PageHeader } from "@/components/shared/page-header";
import { ModuleNav } from "@/components/shared/module-nav";
import { peopleListView, peopleRedirectsToDirectory } from "@/lib/people-nav";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { PoshPayBanner, SeatMeter } from "@/components/people/seat-meter";
import { LegacyProbationReview } from "@/components/people/legacy-probation-review";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DeleteMistakenDialog } from "@/components/people/delete-mistaken-dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ALL_DEPTS = "__all__";
const DEFAULT_PAGE_SIZE = 10;

// Mirrors the backend RBAC on POST /api/v1/employees (company_admin,
// hr_manager). Gates only the "Add employee" affordance — the directory
// itself is visible to every tenant user via GET /api/v1/directory.
const CAN_ADD_PEOPLE = new Set<Role>(["company_admin", "hr_manager"]);

const peopleSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  // Server-side filters (bug B-A6) — kept in the URL so a search/department
  // scope survives refresh, back/forward, and shared links. Empty/ALL drop
  // out of the URL.
  q: z.string().trim().min(1).optional().catch(undefined),
  department: z.string().trim().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/people/")({
  validateSearch: peopleSearchSchema,
  // A9 hover-prefetch. The primary list is the SERVER-paginated/filtered
  // directory page — its committed filters live in the (validated) URL
  // search, surfaced to the loader via loaderDeps. Mirrors useDirectory()'s
  // key normalisation exactly (page ?? 1, trimmed-or-"" filters). The
  // full-roster manager-name resolver is a slower secondary → fire-and-
  // forget prefetch, never awaited.
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    q: search.q?.trim() ?? "",
    department: search.department?.trim() ?? "",
  }),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery({
      queryKey: directoryKeys.list(true),
      queryFn: ({ signal }) =>
        directoryApi.listDirectory({ includeInactive: true }, signal),
    });
    return context.queryClient
      .ensureQueryData({
        queryKey: directoryKeys.list(
          true,
          deps.page,
          DEFAULT_PAGE_SIZE,
          deps.q || undefined,
          deps.department || undefined,
        ),
        queryFn: ({ signal }) =>
          directoryApi.listDirectory(
            {
              includeInactive: true,
              page: deps.page,
              perPage: DEFAULT_PAGE_SIZE,
              q: deps.q || undefined,
              department: deps.department || undefined,
            },
            signal,
          ),
      })
      .catch(() => undefined);
  },
  component: PeopleRoute,
});

function PeopleRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  // Not HR and not a manager: nothing to manage here — the Directory.
  if (peopleRedirectsToDirectory(user?.role)) {
    return <Navigate to="/app/$tenantSlug/directory" params={{ tenantSlug }} replace />;
  }
  return <PeopleList />;
}

function PeopleList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const navigate = Route.useNavigate();
  const { page: urlPage, q: urlQ, department: urlDept } = Route.useSearch();
  const page = urlPage ?? 1;
  // The URL holds the COMMITTED filters; these feed the server query.
  const committedSearch = urlQ ?? "";
  const dept = urlDept ?? ALL_DEPTS;

  const canAdd = !!user?.role && CAN_ADD_PEOPLE.has(user.role);
  const hrAdmin = isHRAdmin(user?.role);
  // HR sees the whole company ("People"); a manager only their reports,
  // direct and indirect ("My Team", ?reports_of=me on the server).
  const view = peopleListView(user?.role);
  // POSH only (migration 162): no change requests / probation review.
  const poshOnly = usePoshOnly();
  // "Remove people added by mistake" (prospect drop-off review 2026-10):
  // HR ticks people across pages (e.g. one test import) and deletes them
  // together; the dialog checks first and keeps anyone with history.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Map<number, string>>(() => new Map());
  function toggleSelected(id: number, name: string, on: boolean) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (on) next.set(id, name);
      else next.delete(id);
      return next;
    });
  }

  // The search box is responsive local state; its debounced value is what we
  // commit to the URL (and therefore the server query) so we fire one request
  // after the user pauses, not one per keystroke (bug B-A6). Seeded from the
  // URL so a refresh / shared link restores the box.
  const [searchInput, setSearchInput] = useState(committedSearch);
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 300);

  // include_inactive so a deactivated colleague still resolves (e.g. as
  // someone's recorded manager); we visually flag them as Inactive.
  // Search + department are pushed to the SERVER (bug B-A6) so they span the
  // whole roster, not just the loaded page.
  const dir = useDirectory({
    includeInactive: true,
    page,
    perPage: DEFAULT_PAGE_SIZE,
    q: committedSearch,
    department: dept === ALL_DEPTS ? undefined : dept,
    reportsOfMe: view.teamOnly,
    // Wait for the role so a manager never flashes the whole company.
    enabled: !!user?.role,
  });

  // FULL-roster id→name resolver (docs/51 manager-link bug). `dir` above is
  // paginated/filtered, so its byId map only covers the current page — a
  // manager not on the page would degrade to "User #<id>". This unpaginated,
  // unfiltered directory (the hook's documented picker/resolver mode) resolves
  // a manager's real name regardless of which page they sit on. includeInactive
  // so a deactivated manager still resolves.
  const managerNames = useDirectory({ includeInactive: true });

  // Tenant-wide DISTINCT departments from the server (every department, not
  // just the current page's — bug B-A6). Fall back to deriving from the loaded
  // rows only if the backend didn't send the list (e.g. non-paginated call).
  // The Departments list (migration 169) is the source when it has loaded —
  // switched-off ones too, since former staff can still sit in them.
  const deptList = useDepartments({ includeInactive: true });
  const departments = useMemo(() => {
    if (deptList.data) return deptList.data.departments.map((d) => d.name);
    if (dir.departments) return dir.departments;
    const s = new Set<string>();
    for (const e of dir.entries) {
      if (e.department) s.add(e.department);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [deptList.data, dir.departments, dir.entries]);

  // The server applies q + department AND the order (active first, then by
  // name) across the whole roster — re-sorting here only reordered the one
  // loaded page (an active person sat on page 2 behind inactive ones).
  const rows = dir.entries;

  const hasFilter = committedSearch !== "" || dept !== ALL_DEPTS;

  const totalPages = Math.max(1, Math.ceil(dir.total / DEFAULT_PAGE_SIZE));
  // Showing range based on (filtered) total, adjusted for current page.
  const rangeStart = dir.total === 0 ? 0 : (page - 1) * DEFAULT_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * DEFAULT_PAGE_SIZE, dir.total);
  const employeePlural = dir.total === 1 ? "employee" : "employees";

  // Commit the debounced search to the URL → the server query. A NEW search
  // resets to page 1 (the filtered result set is a different roster); replace:
  // true so each keystroke-batch doesn't spam the back-stack. Skip when the
  // committed value already matches (avoids a redundant navigate on mount /
  // back-forward).
  //
  // Race guard: `navigate` from Route.useNavigate() is pinned to THIS list
  // route, so a debounced commit that fires while the user's click on a row
  // is still loading the next route (the list stays mounted until the new
  // route's loader resolves) would navigate BACK to the list and undo the
  // click. Once any navigation to a different path begins, stop committing.
  const router = useRouter();
  const leavingRef = useRef(false);
  useEffect(() => {
    const listPath = router.state.location.pathname;
    return router.subscribe("onBeforeNavigate", (e) => {
      if (e.toLocation.pathname !== listPath) leavingRef.current = true;
    });
  }, [router]);
  useEffect(() => {
    if (debouncedSearch === committedSearch) return;
    if (leavingRef.current) return;
    navigate({
      search: (prev) => ({
        ...prev,
        q: debouncedSearch || undefined,
        page: undefined,
      }),
      replace: true,
    });
  }, [debouncedSearch, committedSearch, navigate]);

  function onDeptChange(next: string) {
    navigate({
      search: (prev) => ({
        ...prev,
        department: next === ALL_DEPTS ? undefined : next,
        // A new department scope is a different roster → back to page 1.
        page: undefined,
      }),
      replace: false,
    });
  }

  // One click back to the whole roster from a search / department filter
  // that matched nobody (people QA #14).
  function clearFilters() {
    setSearchInput("");
    navigate({
      search: (prev) => ({
        ...prev,
        q: undefined,
        department: undefined,
        page: undefined,
      }),
      replace: false,
    });
  }

  function goToPage(p: number) {
    navigate({
      // Reducer form (the router types `search` as a ParamsReducerFn here).
      // page:undefined drops it from the URL (back to the default page 1),
      // matching the optional `page?: number` search schema. Filters are
      // server-side now and span every page, so paging KEEPS them (prev spread).
      search: (prev) => ({ ...prev, page: p === 1 ? undefined : p }),
      replace: false,
    });
  }

  return (
    <>
      <PageHeader
        title={view.title === "My Team" ? "My Team" : "Employees"}
        description={view.description}
        actions={
          canAdd ? (
            // Phones: the primary action first and full width, then Export +
            // Import CSV as an even pair. sm+: one row, primary last.
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
              {hrAdmin && <EmployeeExportMenu />}
              {hrAdmin && (
                <Button asChild variant="outline" className="gap-1.5">
                  <Link
                    to="/app/$tenantSlug/bulk-imports/new"
                    params={{ tenantSlug }}
                  >
                    <Upload className="h-4 w-4" />
                    Import CSV
                  </Link>
                </Button>
              )}
              <Button asChild className="order-first col-span-2 gap-1.5 sm:order-none">
                <Link to="/app/$tenantSlug/people/new" params={{ tenantSlug }}>
                  <UserPlus className="h-4 w-4" />
                  Add Employee
                </Link>
              </Button>
            </div>
          ) : undefined
        }
      />

      <ModuleNav />

      {hrAdmin && <PoshPayBanner className="mb-5" />}
      {hrAdmin && <SeatMeter className="mb-5" skipPoshPay />}
      <LegacyProbationReview enabled={hrAdmin && !poshOnly} className="mb-5" />


      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, job title or team"
            className="pl-8"
            aria-label={view.teamOnly ? "Search your team" : "Search the directory"}
          />
        </div>
        {hrAdmin && (
          <Button
            type="button"
            variant={selectMode ? "secondary" : "outline"}
            className="gap-1.5"
            onClick={() => {
              setSelectMode((v) => !v);
              setSelected(new Map());
            }}
            data-testid="people-select-mode"
          >
            <ListChecks aria-hidden className="h-4 w-4" />
            {selectMode ? "Done" : "Remove Added by Mistake"}
          </Button>
        )}
        <Select value={dept} onValueChange={onDeptChange}>
          <SelectTrigger aria-label="Department" className="w-full sm:w-56">
            <SelectValue placeholder="All departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DEPTS}>All Departments</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hrAdmin && selectMode && (
        <Alert className="mb-5" data-testid="people-select-banner">
          <Trash2 aria-hidden className="h-4 w-4" />
          <AlertTitle>Remove people added by mistake</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              Tick the people to remove — for example everyone from a test import. Anyone with
              history in WorkRight (leave, attendance, payroll, documents, or who has signed
              in) is kept: deactivate them instead. You&apos;ll see who goes and who stays
              before anything is deleted.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium" aria-live="polite">
                {selected.size} selected
              </span>
              <DeleteMistakenDialog
                employeeIds={[...selected.keys()]}
                onDeleted={() => setSelected(new Map())}
                trigger={
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="gap-1.5"
                    disabled={selected.size === 0}
                    data-testid="people-delete-selected"
                  >
                    <Trash2 aria-hidden className="h-4 w-4" />
                    Delete Selected
                  </Button>
                }
              />
              {selected.size > 0 && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setSelected(new Map())}>
                  Clear Selection
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {dir.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }, (_, i) => `skel-${i}`).map((k) => (
            <Card key={k}>
              <CardHeader>
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="mt-2 h-4 w-1/2" />
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {dir.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the directory</AlertTitle>
          <AlertDescription>
            {toApiError(dir.error).message}
          </AlertDescription>
        </Alert>
      )}

      {/* Genuinely empty roster (no filter applied) — onboarding empty state.
          A zero-result FILTER is handled below as "No people match" so we don't
          tell the user to add their first employee just because a search missed. */}
      {!dir.isLoading && !dir.isError && dir.total === 0 && !hasFilter && (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <Users className="h-6 w-6" />
            </span>
            <p className="max-w-sm text-sm text-muted-foreground">
              {canAdd
                ? "It's just you so far. Add your team so they can use leave, expenses, POSH and more."
                : view.teamOnly
                  ? "No one reports to you yet. Ask HR to set you as their manager."
                  : "No colleagues yet. As your HR team adds employees they will appear here."}
            </p>
            {canAdd && (
              <Button asChild className="gap-1.5">
                <Link to="/app/$tenantSlug/people/new" params={{ tenantSlug }}>
                  <UserPlus className="h-4 w-4" />
                  Add Your First Employee
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter applied but nothing matched (server-side) — distinct from an
          empty roster. */}
      {!dir.isLoading && !dir.isError && dir.total === 0 && hasFilter && (
        <EmptyState
          icon={SearchX}
          title="No people match these filters"
          description="Try a different name, job title or team — or clear the filters to see everyone."
          action={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearFilters}
              data-testid="people-clear-filters"
            >
              Clear Filters
            </Button>
          }
        />
      )}

      {!dir.isLoading && !dir.isError && dir.total > 0 && (
        <>
          {/* Total count / range indicator. With a filter the total is the
              filtered set; show the page range either way (server-paginated). */}
          <p
            className="mb-3 text-sm text-muted-foreground"
            aria-live="polite"
            data-testid="people-count"
          >
            {hasFilter
              ? `${dir.total} ${employeePlural} match · showing ${rangeStart}–${rangeEnd}`
              : `Showing ${rangeStart}–${rangeEnd} of ${dir.total} ${employeePlural}`}
          </p>

          {rows.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title="No people match these filters"
              description="Try a different name or clear the department filter to see more of your team."
              action={
                hasFilter ? (
                  <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="space-y-3">
              {rows.map((e) => (
                <Card key={e.id} id={`person-${e.id}`} className="pointer-coarse:relative">
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      {hrAdmin && selectMode && e.id !== dir.myId && (
                        <Checkbox
                          className="mt-1 pointer-coarse:z-10"
                          checked={selected.has(e.id)}
                          onCheckedChange={(v) => toggleSelected(e.id, e.display_name, v === true)}
                          aria-label={`Select ${e.display_name}`}
                          data-testid={`people-select-${e.id}`}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base">
                          <Link
                            to="/app/$tenantSlug/people/$id"
                            params={{ tenantSlug, id: String(e.id) }}
                            className="underline-offset-4 hover:text-primary hover:underline pointer-coarse:after:absolute pointer-coarse:after:inset-0"
                          >
                            {e.display_name}
                          </Link>
                          {e.id === dir.myId && (
                            <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-secondary-foreground">
                              You
                            </span>
                          )}
                        </CardTitle>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {e.employee_code ? `${e.employee_code} · ` : ""}
                          {e.designation || "—"}
                          {e.department ? ` · ${e.department}` : ""}
                        </p>
                        {e.manager_id != null && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Reports to{" "}
                            <Link
                              to="/app/$tenantSlug/people/$id"
                              params={{
                                tenantSlug,
                                id: String(e.manager_id),
                              }}
                              className="font-medium text-primary underline-offset-4 hover:underline pointer-coarse:relative pointer-coarse:z-10 pointer-coarse:after:absolute pointer-coarse:after:-inset-3"
                            >
                              {managerNames.label(e.manager_id, null, false)}
                            </Link>
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {!e.is_active && (
                          <StatusBadge tone="neutral" label="Inactive" />
                        )}
                        {canAdd && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="gap-1.5 pointer-coarse:relative pointer-coarse:z-10"
                          >
                            <Link
                              to="/app/$tenantSlug/people/$id/edit"
                              params={{ tenantSlug, id: String(e.id) }}
                            >
                              <Edit3 aria-hidden className="h-3.5 w-3.5" />
                              Edit
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination controls — only shown when more than one page */}
          {totalPages > 1 && (
            <div
              className="mt-6 flex items-center justify-between gap-4"
              aria-label="Pagination"
            >
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>

              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
                aria-label="Next page"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
